import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

export type ToastVariant = 'success' | 'error' | 'info'

type ToastProps = {
  message: string
  variant?: ToastVariant
  /** ms until auto-dismiss (default 4500). Set 0 to disable auto-dismiss */
  duration?: number
  onDismiss: () => void
}

/** Opaque `bg-background` — theme `--card` is translucent in dark mode and toasts were unreadable over the header. */
const variantStyles: Record<ToastVariant, string> = {
  success:
    'border border-[color-mix(in_oklch,var(--success)_40%,var(--border))] bg-background text-foreground shadow-[var(--shadow-modal)] ring-1 ring-[color-mix(in_oklch,var(--success)_22%,var(--border))]',
  error:
    'border-2 border-destructive/80 bg-background text-foreground shadow-[var(--shadow-modal)] ring-1 ring-destructive/25',
  info:
    'border border-border bg-background text-foreground shadow-[var(--shadow-modal)] ring-1 ring-border',
}

/** Single toast, fixed top-right. Parent controls visibility; auto-dismiss after `duration` (default 4.5s). */
export function Toast({ message, variant = 'info', duration = 4500, onDismiss }: ToastProps) {
  useEffect(() => {
    if (duration <= 0) return
    const t = window.setTimeout(onDismiss, duration)
    return () => window.clearTimeout(t)
  }, [duration, onDismiss, message])

  const node = (
    <div
      role="status"
      className={cn(
        'fixed top-20 right-4 z-[300] max-w-sm rounded-2xl px-4 py-3 sm:right-6 motion-reduce:transition-none',
        variantStyles[variant],
      )}
    >
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm font-medium leading-snug">{message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted-foreground hover:bg-muted hover:text-foreground shrink-0 rounded-lg px-2 py-0.5 text-xs font-semibold transition"
        >
          Dismiss
        </button>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}
